import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import {
  API_URL,
  getAuthHeaders,
  handleApiError,
} from "../../services/api/api";
import { debugThrottled, expenseUtils } from "../../lib";
import {
  buildAmortizationSchedule,
  clampNumber,
  computeAnnualPropertyTax,
  computeDownPaymentAmount,
  computeLoanAmount,
  computeMonthlyFromAnnual,
  computeMonthlyMortgagePayment,
  computeMortgageTaxImpact,
  getCreditScoreBand,
  getRateAdjustmentPctForCreditScore,
  sumAmortizationInterest,
} from "../../lib/mortgageCalculatorUtils";
import {
  calculateIncomeDetails,
  TaxProfile,
} from "../../lib/taxCalculatorUtils";
import {
  onTaxProfilesChanged,
  taxProfilesDB,
} from "../../services/database/taxProfilesDB";
import PageShareModal from "../common/PageShareModal";
import { pageShareAPI } from "../../services/api/api";
import { useShareAction } from "../../contexts/ShareActionContext";
import {
  mortgageCalculatorProfilesDB,
  MortgageCalculatorProfile,
  MortgageCalculatorSettings,
  onMortgageCalculatorProfilesChanged,
} from "../../services/database/mortgageCalculatorProfilesDB";

type MortgageRateResponse = {
  source: string;
  seriesId: string;
  observationDate: string;
  ratePct: number;
  cached?: boolean;
  stale?: boolean;
};

const formatCurrency = (value: number) =>
  expenseUtils.formatCurrency(Number.isFinite(value) ? value : 0);

const formatPct = (valuePct: number, digits = 2) =>
  `${(Number.isFinite(valuePct) ? valuePct : 0).toFixed(digits)}%`;

const formatRateDecimal = (value: number, digits = 6) =>
  (Number.isFinite(value) ? value : 0).toFixed(digits);

const generateMortgageProfileId = (): string =>
  `mortgage_profile_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const SaveMortgageProfileDialog = React.memo(
  function SaveMortgageProfileDialog({
    open,
    onClose,
    placeholder,
    onSave,
  }: {
    open: boolean;
    onClose: () => void;
    placeholder: string;
    onSave: (name: string) => Promise<void>;
  }) {
    const [name, setName] = useState("");

    useEffect(() => {
      if (!open) return;
      setName("");
    }, [open]);

    const trimmed = name.trim();

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Save Mortgage Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Profile Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (!trimmed) return;
              void onSave(trimmed);
            }}
            placeholder={placeholder}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!trimmed}
            onClick={() => void onSave(trimmed)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const MortgageCalculator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();
  const shareId = searchParams.get("shareId");
  const isSharedView = Boolean(shareId);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  const [sharedMode, setSharedMode] = useState(false);
  const [sharedTaxProfile, setSharedTaxProfile] = useState<TaxProfile | null>(
    null
  );

  const [mortgageProfiles, setMortgageProfiles] = useState<
    MortgageCalculatorProfile[]
  >([]);
  const [mortgageProfileId, setMortgageProfileId] = useState<string>("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Core inputs
  const [homePrice, setHomePrice] = useState<number>(725000);
  const [creditScore, setCreditScore] = useState<number>(660);
  const [downPaymentPct, setDownPaymentPct] = useState<number>(20);
  const [termYears, setTermYears] = useState<number>(30);

  // Bend, OR defaults
  const [propertyTaxRatePct, setPropertyTaxRatePct] = useState<number>(0.72);
  const [annualHomeownersInsurance, setAnnualHomeownersInsurance] =
    useState<number>(1300);

  // Optional override
  const [interestRateMode, setInterestRateMode] = useState<
    "modeled" | "override"
  >("modeled");
  const [interestRateInputPct, setInterestRateInputPct] = useState<string>("");

  // Notes (not used in calculations currently)
  const [homeType, setHomeType] = useState<string>("single_family");
  const [bedrooms, setBedrooms] = useState<number>(4);
  const [bathrooms, setBathrooms] = useState<number>(3);
  const [squareFeet, setSquareFeet] = useState<number>(2200);

  // Mortgage rate fetch
  const [rateInfo, setRateInfo] = useState<MortgageRateResponse | null>(null);
  const [rateLoading, setRateLoading] = useState<boolean>(false);
  const [rateError, setRateError] = useState<string>("");

  const fetchMortgageRate = useCallback(async () => {
    setRateLoading(true);
    setRateError("");
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${API_URL}/tools/mortgage-rate?series=MORTGAGE30US`,
        { method: "GET", headers }
      );
      if (!res.ok) {
        await handleApiError(res);
        throw new Error(`Failed to fetch mortgage rate (${res.status})`);
      }
      const data = (await res.json()) as MortgageRateResponse;
      if (!Number.isFinite(data?.ratePct)) {
        throw new Error("Mortgage rate response missing ratePct");
      }
      console.debug("[MortgageCalculator] Mortgage rate fetched", data);
      setRateInfo(data);
    } catch (error: any) {
      console.error(
        "[MortgageCalculator] Failed to fetch mortgage rate",
        error
      );
      setRateError(
        typeof error?.message === "string"
          ? error.message
          : "Failed to fetch mortgage rate."
      );
    } finally {
      setRateLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMortgageRate();
  }, [fetchMortgageRate]);

  // Tax profiles (optional)
  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>([]);
  const [taxProfileId, setTaxProfileId] = useState<string>("manual");
  const [manualFederalMarginalRatePct, setManualFederalMarginalRatePct] =
    useState<number>(22);
  const [manualStateMarginalRatePct, setManualStateMarginalRatePct] =
    useState<number>(8.75);
  const [saltCap, setSaltCap] = useState<number>(10000);
  const [otherSaltUsed, setOtherSaltUsed] = useState<number>(0);
  const [mortgageInterestDebtLimit, setMortgageInterestDebtLimit] =
    useState<number>(750000);

  const updateUrlWithProfile = useCallback(
    (profileId: string) => {
      const next = new URLSearchParams(searchParams);
      if (profileId) {
        next.set("profile", profileId);
      } else {
        next.delete("profile");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const buildCurrentSettings = useCallback((): MortgageCalculatorSettings => {
    return {
      version: 1,
      homePrice,
      creditScore,
      downPaymentPct,
      termYears,
      propertyTaxRatePct,
      annualHomeownersInsurance,
      interestRateMode,
      interestRateInputPct,
      homeType,
      bedrooms,
      bathrooms,
      squareFeet,
      taxProfileId,
      manualFederalMarginalRatePct,
      manualStateMarginalRatePct,
      saltCap,
      otherSaltUsed,
      mortgageInterestDebtLimit,
    };
  }, [
    annualHomeownersInsurance,
    bathrooms,
    bedrooms,
    creditScore,
    downPaymentPct,
    homePrice,
    homeType,
    interestRateInputPct,
    interestRateMode,
    manualFederalMarginalRatePct,
    manualStateMarginalRatePct,
    mortgageInterestDebtLimit,
    otherSaltUsed,
    propertyTaxRatePct,
    saltCap,
    squareFeet,
    taxProfileId,
    termYears,
  ]);

  const applySettings = useCallback((raw: any) => {
    const settings = raw as Partial<MortgageCalculatorSettings>;
    if (typeof settings.homePrice === "number") {
      setHomePrice(settings.homePrice);
    }
    if (typeof settings.creditScore === "number")
      setCreditScore(settings.creditScore);
    if (typeof settings.downPaymentPct === "number")
      setDownPaymentPct(settings.downPaymentPct);
    if (typeof settings.termYears === "number") {
      setTermYears(settings.termYears);
    }
    if (typeof settings.propertyTaxRatePct === "number")
      setPropertyTaxRatePct(settings.propertyTaxRatePct);
    if (typeof settings.annualHomeownersInsurance === "number")
      setAnnualHomeownersInsurance(settings.annualHomeownersInsurance);
    if (
      settings.interestRateMode === "modeled" ||
      settings.interestRateMode === "override"
    ) {
      setInterestRateMode(settings.interestRateMode);
    }
    if (typeof settings.interestRateInputPct === "string")
      setInterestRateInputPct(settings.interestRateInputPct);
    if (typeof settings.homeType === "string") setHomeType(settings.homeType);
    if (typeof settings.bedrooms === "number") setBedrooms(settings.bedrooms);
    if (typeof settings.bathrooms === "number")
      setBathrooms(settings.bathrooms);
    if (typeof settings.squareFeet === "number")
      setSquareFeet(settings.squareFeet);
    if (typeof settings.taxProfileId === "string")
      setTaxProfileId(settings.taxProfileId);
    if (typeof settings.manualFederalMarginalRatePct === "number")
      setManualFederalMarginalRatePct(settings.manualFederalMarginalRatePct);
    if (typeof settings.manualStateMarginalRatePct === "number")
      setManualStateMarginalRatePct(settings.manualStateMarginalRatePct);
    if (typeof settings.saltCap === "number") setSaltCap(settings.saltCap);
    if (typeof settings.otherSaltUsed === "number")
      setOtherSaltUsed(settings.otherSaltUsed);
    if (typeof settings.mortgageInterestDebtLimit === "number")
      setMortgageInterestDebtLimit(settings.mortgageInterestDebtLimit);
  }, []);

  const selectedMortgageProfile = useMemo(() => {
    if (!mortgageProfileId) return null;
    return mortgageProfiles.find((p) => p.id === mortgageProfileId) || null;
  }, [mortgageProfileId, mortgageProfiles]);

  useEffect(() => {
    if (sharedMode) return;
    if (!selectedMortgageProfile) return;
    applySettings(selectedMortgageProfile.settings);
  }, [applySettings, selectedMortgageProfile, sharedMode]);

  useEffect(() => {
    if (isSharedView) return;
    setShareHandler(() => setShareDialogOpen(true));
    return () => setShareHandler(null);
  }, [isSharedView, setShareHandler]);

  const applySharedPayload = useCallback(
    (payload: any) => {
      if (payload?.settings) {
        applySettings(payload.settings);
      }
      if (payload?.taxProfile?.incomeInfo) {
        setSharedTaxProfile({
          id: "shared",
          name:
            typeof payload?.taxProfile?.name === "string"
              ? payload.taxProfile.name
              : "Shared Tax Profile",
          incomeInfo: payload.taxProfile.incomeInfo,
          createdAt: Date.now(),
        });
      }
    },
    [applySettings]
  );

  const handleUnlockShare = useCallback(async () => {
    if (!pendingShareId || !unlockPassword.trim()) {
      setUnlockError("Password required.");
      return;
    }
    setUnlockError("");
    try {
      const result = await pageShareAPI.validate(
        pendingShareId,
        unlockPassword.trim()
      );
      applySharedPayload(result.payload);
      setUnlockDialogOpen(false);
      setPendingShareId(null);
      setUnlockPassword("");
    } catch (error) {
      console.error("[MortgageCalculator] Failed to unlock share", error);
      setUnlockError("Invalid password.");
    }
  }, [applySharedPayload, pendingShareId, unlockPassword]);

  useEffect(() => {
    if (!shareId) return;
    setSharedMode(true);
    const loadShare = async () => {
      try {
        const info = await pageShareAPI.getInfo(shareId);
        if (info.pageType !== "mortgage") {
          setUnlockError("This share link is not for Mortgage Calculator.");
          return;
        }
        if (info.requiresPassword) {
          setPendingShareId(shareId);
          setUnlockDialogOpen(true);
          return;
        }
        const result = await pageShareAPI.validate(shareId);
        applySharedPayload(result.payload);
      } catch (error) {
        console.error("[MortgageCalculator] Failed to load share", error);
        setUnlockError("Failed to load shared data.");
      }
    };
    void loadShare();
  }, [applySharedPayload, shareId]);

  useEffect(() => {
    if (sharedMode) return;
    let mounted = true;
    const load = async () => {
      const remote = await mortgageCalculatorProfilesDB.getAll();
      if (!mounted) return;
      setMortgageProfiles(remote);
    };
    void load();
    const unsub = onMortgageCalculatorProfilesChanged(() => void load());
    return () => {
      mounted = false;
      unsub();
    };
  }, [sharedMode]);

  useEffect(() => {
    if (sharedMode) return;
    if (mortgageProfiles.length === 0) return;

    const urlProfileId = searchParams.get("profile");
    if (
      urlProfileId &&
      mortgageProfiles.some((p) => p.id === urlProfileId) &&
      mortgageProfileId !== urlProfileId
    ) {
      setMortgageProfileId(urlProfileId);
      return;
    }

    if (!mortgageProfileId) {
      setMortgageProfileId(mortgageProfiles[0]?.id || "");
      return;
    }
    if (mortgageProfiles.some((p) => p.id === mortgageProfileId)) return;
    setMortgageProfileId(mortgageProfiles[0]?.id || "");
  }, [mortgageProfiles, mortgageProfileId, searchParams, sharedMode]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const remote = await taxProfilesDB.getAll();
      if (!mounted) return;
      setTaxProfiles(remote);
      if (taxProfileId === "manual") return;
      if (remote.some((p) => p.id === taxProfileId)) return;
      setTaxProfileId("manual");
    };
    void load();
    const unsub = onTaxProfilesChanged(() => void load());
    return () => {
      mounted = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTaxProfile = useMemo(() => {
    if (sharedMode && sharedTaxProfile) return sharedTaxProfile;
    if (taxProfileId === "manual") return null;
    return taxProfiles.find((p) => p.id === taxProfileId) || null;
  }, [sharedMode, sharedTaxProfile, taxProfileId, taxProfiles]);

  const incomeDetails = useMemo(() => {
    if (!selectedTaxProfile) return null;
    try {
      return calculateIncomeDetails(selectedTaxProfile.incomeInfo);
    } catch (error) {
      console.error("[MortgageCalculator] Failed to calculate income details", {
        error,
        taxProfileId: selectedTaxProfile.id,
      });
      return null;
    }
  }, [selectedTaxProfile]);

  const federalMarginalRate =
    incomeDetails?.yearlyIncome.federalMarginalRate ??
    clampNumber(manualFederalMarginalRatePct / 100, 0, 1);
  const stateMarginalRate =
    incomeDetails?.yearlyIncome.stateMarginalRate ??
    clampNumber(manualStateMarginalRatePct / 100, 0, 1);

  // Interest rate model
  const creditBand = useMemo(
    () => getCreditScoreBand(creditScore),
    [creditScore]
  );
  const creditAdjPct = useMemo(
    () => getRateAdjustmentPctForCreditScore(creditScore),
    [creditScore]
  );
  const baseRatePct = rateInfo?.ratePct ?? 0;
  const modeledRatePct = Math.max(0, baseRatePct + creditAdjPct);

  // Keep the Interest Rate field populated. When mode is "modeled", the field
  // stays synced to modeledRatePct. When user edits, it becomes an override.
  useEffect(() => {
    if (interestRateMode !== "modeled") return;
    setInterestRateInputPct(modeledRatePct.toFixed(3));
  }, [interestRateMode, modeledRatePct]);

  const parsedInterestRateInput = Number.parseFloat(interestRateInputPct);
  const overrideRatePct =
    interestRateMode === "override" && Number.isFinite(parsedInterestRateInput)
      ? Math.max(0, parsedInterestRateInput)
      : null;
  const finalRatePct = overrideRatePct ?? modeledRatePct;

  // Core math
  const downPaymentAmount = useMemo(
    () => computeDownPaymentAmount(homePrice, downPaymentPct),
    [homePrice, downPaymentPct]
  );
  const loanAmount = useMemo(
    () => computeLoanAmount(homePrice, downPaymentAmount),
    [homePrice, downPaymentAmount]
  );
  const monthlyRateDecimal = finalRatePct / 100 / 12;
  const totalPayments = Math.max(0, Math.round(termYears * 12));

  const monthlyPrincipalAndInterest = useMemo(
    () =>
      computeMonthlyMortgagePayment({
        principal: loanAmount,
        annualRatePct: finalRatePct,
        termYears,
      }),
    [loanAmount, finalRatePct, termYears]
  );

  const annualPropertyTax = useMemo(
    () => computeAnnualPropertyTax(homePrice, propertyTaxRatePct),
    [homePrice, propertyTaxRatePct]
  );
  const monthlyPropertyTax = useMemo(
    () => computeMonthlyFromAnnual(annualPropertyTax),
    [annualPropertyTax]
  );
  const monthlyInsurance = useMemo(
    () => computeMonthlyFromAnnual(annualHomeownersInsurance),
    [annualHomeownersInsurance]
  );

  const monthlyPITI =
    monthlyPrincipalAndInterest + monthlyPropertyTax + monthlyInsurance;

  const totalPrincipalPaidLife = loanAmount;
  const totalInterestPaidLife = Math.max(
    0,
    monthlyPrincipalAndInterest * totalPayments - loanAmount
  );
  const totalPaidPrincipalAndInterestLife =
    totalPrincipalPaidLife + totalInterestPaidLife;

  const firstYearSchedule = useMemo(
    () =>
      buildAmortizationSchedule({
        principal: loanAmount,
        annualRatePct: finalRatePct,
        termYears,
        months: 12,
      }),
    [loanAmount, finalRatePct, termYears]
  );
  const firstYearInterest = useMemo(
    () => sumAmortizationInterest(firstYearSchedule),
    [firstYearSchedule]
  );

  const taxImpact = useMemo(() => {
    return computeMortgageTaxImpact({
      loanAmount,
      firstYearInterest,
      annualPropertyTax,
      settings: {
        federalMarginalRate,
        stateMarginalRate,
        saltCap,
        otherSaltUsed,
        mortgageInterestDebtLimit,
      },
    });
  }, [
    annualPropertyTax,
    federalMarginalRate,
    firstYearInterest,
    loanAmount,
    mortgageInterestDebtLimit,
    otherSaltUsed,
    saltCap,
    stateMarginalRate,
  ]);

  const monthlyTaxSavings = taxImpact.totalAnnualTaxSavings / 12;
  const afterTaxMonthlyCost = monthlyPITI - monthlyTaxSavings;

  useEffect(() => {
    debugThrottled(
      "mortgageCalc:state",
      2000,
      "[DEBUG] MortgageCalculator state",
      {
        homePrice,
        downPaymentPct,
        creditScore,
        termYears,
        propertyTaxRatePct,
        annualHomeownersInsurance,
        baseRatePct: rateInfo?.ratePct,
        modeledRatePct,
        finalRatePct,
        loanAmount,
        monthlyPITI,
        federalMarginalRate,
        stateMarginalRate,
        monthlyTaxSavings,
        afterTaxMonthlyCost,
      }
    );
  }, [
    afterTaxMonthlyCost,
    annualHomeownersInsurance,
    baseRatePct,
    creditScore,
    downPaymentPct,
    federalMarginalRate,
    finalRatePct,
    homePrice,
    loanAmount,
    modeledRatePct,
    monthlyPITI,
    monthlyTaxSavings,
    propertyTaxRatePct,
    rateInfo?.ratePct,
    stateMarginalRate,
    termYears,
  ]);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* Mortgage Profiles */}
        {sharedMode ? (
          <Alert severity="info">
            Shared view: changes are editable but will not be saved to your
            profiles.
          </Alert>
        ) : (
          <Paper sx={{ p: 2 }} variant="outlined">
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <PersonIcon color="primary" />
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                sx={{ minWidth: 80 }}
              >
                Profiles
              </Typography>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Select Profile</InputLabel>
                <Select
                  value={mortgageProfileId}
                  label="Select Profile"
                  onChange={(e) => {
                    const next = String(e.target.value);
                    setMortgageProfileId(next);
                    updateUrlWithProfile(next);
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {mortgageProfiles.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => setSaveDialogOpen(true)}
                size="small"
              >
                Save As
              </Button>

              {mortgageProfileId ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      const existing = mortgageProfiles.find(
                        (p) => p.id === mortgageProfileId
                      );
                      if (!existing) return;
                      await mortgageCalculatorProfilesDB.upsert({
                        ...existing,
                        settings: buildCurrentSettings(),
                        createdAt: existing.createdAt || Date.now(),
                      });
                    }}
                  >
                    Update
                  </Button>
                  <Tooltip title="Delete this profile" arrow>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        await mortgageCalculatorProfilesDB.delete(
                          mortgageProfileId
                        );
                        setMortgageProfileId("");
                        updateUrlWithProfile("");
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : null}

              {mortgageProfiles.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: "auto" }}
                >
                  {mortgageProfiles.length} saved profile
                  {mortgageProfiles.length !== 1 ? "s" : ""}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        )}

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Mortgage Calculator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Uses today&apos;s national base rate (Federal Reserve Bank of St.
            Louis FRED) plus a transparent credit-score adjustment. Edit any
            assumption below.
          </Typography>
        </Box>

        {rateError ? (
          <Alert severity="warning">
            Mortgage rate fetch failed: {rateError}
          </Alert>
        ) : null}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchMortgageRate()}
            disabled={rateLoading}
          >
            Refresh Base Rate
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            Base rate: <b>{rateInfo ? formatPct(rateInfo.ratePct, 2) : "-"}</b>{" "}
            {rateInfo?.observationDate
              ? `as of ${rateInfo.observationDate}`
              : ""}
          </Typography>
        </Stack>

        <Divider />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Inputs
          </Typography>

          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Home Price"
                value={String(homePrice)}
                onChange={(e) =>
                  setHomePrice(
                    Number.isFinite(Number(e.target.value))
                      ? Math.max(0, Number(e.target.value))
                      : 0
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                fullWidth
              />

              <TextField
                label="Down Payment (%)"
                value={String(downPaymentPct)}
                onChange={(e) =>
                  setDownPaymentPct(
                    clampNumber(Number(e.target.value) || 0, 0, 100)
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                fullWidth
              />

              <TextField
                label="Term (Years)"
                value={String(termYears)}
                onChange={(e) =>
                  setTermYears(clampNumber(Number(e.target.value) || 0, 0, 40))
                }
                fullWidth
              />
            </Stack>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Credit Score: {Math.round(creditScore)} ({creditBand.label})
              </Typography>
              <Slider
                value={creditScore}
                onChange={(_, value) => setCreditScore(Number(value))}
                min={300}
                max={850}
                step={5}
                valueLabelDisplay="auto"
                sx={{ maxWidth: 640 }}
              />
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Interest Rate (APR %)"
                value={interestRateInputPct}
                onChange={(e) => {
                  setInterestRateMode("override");
                  setInterestRateInputPct(e.target.value);
                }}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={() => setInterestRateMode("modeled")}
                sx={{ whiteSpace: "nowrap" }}
              >
                Use Modeled Rate
              </Button>

              <TextField
                label="Property Tax Rate (Bend, OR) (%)"
                value={String(propertyTaxRatePct)}
                onChange={(e) =>
                  setPropertyTaxRatePct(
                    clampNumber(Number(e.target.value) || 0, 0, 10)
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                fullWidth
              />

              <TextField
                label="Homeowners Insurance (Annual)"
                value={String(annualHomeownersInsurance)}
                onChange={(e) =>
                  setAnnualHomeownersInsurance(
                    clampNumber(Number(e.target.value) || 0, 0, 100000)
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="home-type-label">Home Type</InputLabel>
                <Select
                  labelId="home-type-label"
                  label="Home Type"
                  value={homeType}
                  onChange={(e) => setHomeType(String(e.target.value))}
                >
                  <MenuItem value="single_family">Single Family Home</MenuItem>
                  <MenuItem value="townhouse">Townhouse</MenuItem>
                  <MenuItem value="condo">Condo</MenuItem>
                  <MenuItem value="multi_family">Multi-family</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="# Bedrooms"
                value={String(bedrooms)}
                onChange={(e) =>
                  setBedrooms(clampNumber(Number(e.target.value) || 0, 0, 30))
                }
                fullWidth
              />
              <TextField
                label="# Bathrooms"
                value={String(bathrooms)}
                onChange={(e) =>
                  setBathrooms(clampNumber(Number(e.target.value) || 0, 0, 30))
                }
                fullWidth
              />
              <TextField
                label="Square Feet"
                value={String(squareFeet)}
                onChange={(e) =>
                  setSquareFeet(
                    clampNumber(Number(e.target.value) || 0, 0, 100000)
                  )
                }
                fullWidth
              />
            </Stack>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                Tax Settings (State + Federal)
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="tax-profile-label">Tax Profile</InputLabel>
                  <Select
                    labelId="tax-profile-label"
                    label="Tax Profile"
                    value={taxProfileId}
                    disabled={sharedMode}
                    onChange={(e) => setTaxProfileId(String(e.target.value))}
                  >
                    <MenuItem value="manual">Manual Rates</MenuItem>
                    {taxProfiles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Federal Marginal Rate (%)"
                  value={String(
                    taxProfileId === "manual"
                      ? manualFederalMarginalRatePct
                      : (federalMarginalRate * 100).toFixed(2)
                  )}
                  onChange={(e) =>
                    setManualFederalMarginalRatePct(
                      clampNumber(Number(e.target.value) || 0, 0, 60)
                    )
                  }
                  disabled={taxProfileId !== "manual"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                <TextField
                  label="State Marginal Rate (%)"
                  value={String(
                    taxProfileId === "manual"
                      ? manualStateMarginalRatePct
                      : (stateMarginalRate * 100).toFixed(2)
                  )}
                  onChange={(e) =>
                    setManualStateMarginalRatePct(
                      clampNumber(Number(e.target.value) || 0, 0, 30)
                    )
                  }
                  disabled={taxProfileId !== "manual"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <TextField
                  label="Federal SALT Cap (Annual)"
                  value={String(saltCap)}
                  onChange={(e) =>
                    setSaltCap(
                      clampNumber(Number(e.target.value) || 0, 0, 200000)
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                <TextField
                  label="Other SALT Used (Annual)"
                  value={String(otherSaltUsed)}
                  onChange={(e) =>
                    setOtherSaltUsed(
                      clampNumber(Number(e.target.value) || 0, 0, 200000)
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                <TextField
                  label="Mortgage Interest Debt Limit"
                  value={String(mortgageInterestDebtLimit)}
                  onChange={(e) =>
                    setMortgageInterestDebtLimit(
                      clampNumber(Number(e.target.value) || 0, 0, 5000000)
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Outputs
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Paper sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="subtitle2" color="text.secondary">
                Interest Rate (APR)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {formatPct(finalRatePct, 3)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Modeled = base ({formatPct(baseRatePct, 2)}) + credit adj (
                {formatPct(creditAdjPct, 2)}) = {formatPct(modeledRatePct, 2)}
                {overrideRatePct !== null ? " (overridden)" : ""}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="subtitle2" color="text.secondary">
                Monthly Payment (PITI)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {formatCurrency(monthlyPITI)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                After-tax est: {formatCurrency(afterTaxMonthlyCost)} (tax
                savings {formatCurrency(monthlyTaxSavings)}/mo)
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="subtitle2" color="text.secondary">
                Total Cost (Loan Life)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {formatCurrency(totalPaidPrincipalAndInterestLife)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Principal: {formatCurrency(totalPrincipalPaidLife)} | Interest:{" "}
                {formatCurrency(totalInterestPaidLife)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Principal + interest only ({totalPayments} payments). Excludes
                property tax and insurance.
              </Typography>
            </Paper>
          </Stack>

          <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
              Math Breakdown
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Down payment</TableCell>
                  <TableCell>
                    {formatCurrency(homePrice)} * {formatPct(downPaymentPct, 2)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(downPaymentAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Loan amount</TableCell>
                  <TableCell>
                    {formatCurrency(homePrice)} -{" "}
                    {formatCurrency(downPaymentAmount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(loanAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly rate (decimal)</TableCell>
                  <TableCell>
                    ({formatPct(finalRatePct, 3)} / 100) / 12
                  </TableCell>
                  <TableCell align="right">
                    {formatRateDecimal(monthlyRateDecimal)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell># payments</TableCell>
                  <TableCell>{termYears} * 12</TableCell>
                  <TableCell align="right">{totalPayments}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly P&amp;I</TableCell>
                  <TableCell>P * r / (1 - (1+r)^-n)</TableCell>
                  <TableCell align="right">
                    {formatCurrency(monthlyPrincipalAndInterest)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Annual property tax</TableCell>
                  <TableCell>
                    {formatCurrency(homePrice)} *{" "}
                    {formatPct(propertyTaxRatePct, 2)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(annualPropertyTax)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly property tax</TableCell>
                  <TableCell>
                    {formatCurrency(annualPropertyTax)} / 12
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(monthlyPropertyTax)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly insurance</TableCell>
                  <TableCell>
                    {formatCurrency(annualHomeownersInsurance)} / 12
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(monthlyInsurance)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total monthly (PITI)</TableCell>
                  <TableCell>P&amp;I + taxes + insurance</TableCell>
                  <TableCell align="right">
                    {formatCurrency(monthlyPITI)}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>First-year interest (months 1-12)</TableCell>
                  <TableCell>Sum(interest[m])</TableCell>
                  <TableCell align="right">
                    {formatCurrency(firstYearInterest)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Federal deductible property tax</TableCell>
                  <TableCell>
                    min(property tax, max(0, SALT cap - other SALT))
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(taxImpact.federalDeductiblePropertyTax)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Federal deductible interest</TableCell>
                  <TableCell>
                    first-year interest * min(1, debt limit / loan amount)
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(taxImpact.federalDeductibleInterest)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Federal tax savings (est.)</TableCell>
                  <TableCell>
                    (deductible interest + deductible property tax) *{" "}
                    {formatPct(federalMarginalRate * 100, 2)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(taxImpact.federalTaxSavings)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>State tax savings (est.)</TableCell>
                  <TableCell>
                    (deductible interest + property tax) *{" "}
                    {formatPct(stateMarginalRate * 100, 2)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(taxImpact.stateTaxSavings)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total tax savings (monthly)</TableCell>
                  <TableCell>(federal + state) / 12</TableCell>
                  <TableCell align="right">
                    {formatCurrency(monthlyTaxSavings)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>After-tax monthly cost (est.)</TableCell>
                  <TableCell>PITI - monthly tax savings</TableCell>
                  <TableCell align="right">
                    {formatCurrency(afterTaxMonthlyCost)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
            Sources
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Base mortgage rate:{" "}
            <Link
              href="https://fred.stlouisfed.org/series/MORTGAGE30US"
              target="_blank"
              rel="noopener noreferrer"
            >
              FRED MORTGAGE30US
            </Link>
            . Property tax default (Deschutes County avg effective rate):{" "}
            <Link
              href="https://www.tax-rates.org/oregon/deschutes_county_property_tax"
              target="_blank"
              rel="noopener noreferrer"
            >
              Tax-Rates.org
            </Link>
            . Homeowners insurance default (Oregon avg):{" "}
            <Link
              href="https://insurify.com/homeowners-insurance/oregon/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Insurify
            </Link>
            .
          </Typography>
        </Box>

        <PageShareModal
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          pageType="mortgage"
          payload={{
            version: 1,
            settings: buildCurrentSettings(),
            taxProfile: selectedTaxProfile
              ? {
                  name: selectedTaxProfile.name,
                  incomeInfo: selectedTaxProfile.incomeInfo,
                }
              : null,
          }}
          label={
            mortgageProfiles.find((p) => p.id === mortgageProfileId)?.name ||
            "Mortgage Calculator"
          }
          buildShareUrl={(nextShareId) =>
            `${window.location.origin}/shared/mortgage-calculator?shareId=${nextShareId}`
          }
        />

        {/* Save Profile Dialog */}
        <SaveMortgageProfileDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          placeholder="e.g., Bend SFH 10% down, Condo scenario, etc."
          onSave={async (name) => {
            const profile: MortgageCalculatorProfile = {
              id: generateMortgageProfileId(),
              name,
              settings: buildCurrentSettings(),
              createdAt: Date.now(),
            };
            const saved = await mortgageCalculatorProfilesDB.upsert(profile);
            if (saved) {
              setMortgageProfileId(saved.id);
              updateUrlWithProfile(saved.id);
            }
            setSaveDialogOpen(false);
          }}
        />

        {/* Share Unlock Dialog */}
        <Dialog
          open={unlockDialogOpen}
          onClose={() => setUnlockDialogOpen(false)}
        >
          <DialogTitle>Enter Password</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={unlockPassword}
              onChange={(e) => setUnlockPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void handleUnlockShare();
                }
              }}
            />
            {unlockError ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {unlockError}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void handleUnlockShare()}
            >
              Unlock
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Paper>
  );
};

export default MortgageCalculator;
